import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { getHomepageConfig, getCachedHomepageConfig } from "../lib/publicConfig";
import { formatAZN, pickLocale } from "../lib/format";
import { useI18n } from "../lib/i18n";
import SeoMeta from "../components/storefront/SeoMeta";
import HeroSlider from "../components/storefront/HeroSlider";
import Marquee from "../components/storefront/Marquee";
import CollectionGrid from "../components/storefront/CollectionGrid";
import ProductRail from "../components/storefront/ProductRail";
import AITryOnFeature from "../components/storefront/AITryOnFeature";
import CampaignBanner from "../components/storefront/CampaignBanner";
import ConciergeBand from "../components/storefront/ConciergeBand";
import TrustBenefits from "../components/storefront/TrustBenefits";
import NewsletterBlock from "../components/storefront/NewsletterBlock";
import GalleryStrip from "../components/storefront/GalleryStrip";
import { normalizeProduct } from "../components/storefront/ProductCard";
import { adfAssets, ss26Image } from "../components/storefront/assets";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

/**
 * The homepage.
 *
 * Its order is fixed and deliberate — hero, marquee, the pieces themselves,
 * collections, what sells, AI Try-On, campaigns, concierge, trust, newsletter
 * — and every section below the marquee is a block an editor owns. No campaign
 * copy lives in this file any more: previously eight of twelve sections were
 * hardcoded constants, so changing a homepage headline meant a deploy. A
 * section with no block behind it now renders nothing at all, which is how the
 * sale and the perfumery stay off the page until the catalogue has something
 * real to put in them.
 *
 * Garments come before the collection tiles. A shopper who lands here should
 * meet something buyable before being asked to pick a department; the tiles
 * are navigation, and navigation reads better once you have seen what is on
 * offer.
 *
 * The two product rails are the only sections that are not editor-configured.
 * They stay because they are the one place on the page a shopper meets an
 * actual buyable garment, and they read live from the catalogue rather than
 * from a constant — new arrivals by date, the second rail by the bestseller
 * flag an editor sets on the product itself.
 */
const PRODUCT_LIMIT = 12;

function localText(locale, az, en) {
  return locale === "az" ? az : en;
}

function compactProduct(product, index, locale) {
  const item = normalizeProduct(product, index);
  const title = pickLocale(item.title, locale) || item.name || "";
  return {
    kind: "product",
    // The raw row, so the rail can render the real ProductCard — with quick-add,
    // favourite, Try-On badge and colour swatches, rather than a stripped-down
    // homepage tile a customer cannot buy from.
    product,
    title,
    meta: [item.color, item.fit_type || item.product_type].filter(Boolean).slice(0, 2).join(" · "),
    price: formatAZN(item.min_price, locale),
    image: item.thumbnailSource,
    to: `/products/${item.handle}`,
    isNew: item.is_new_arrival,
  };
}

export default function Home() {
  const { locale } = useI18n();

  // The most-linked page on the site: it needs a description, a canonical and
  // an Organization node, not just a tab title.
  const [orgJsonLd, setOrgJsonLd] = useState(null);
  const [newProducts, setNewProducts] = useState([]);
  const [bestProducts, setBestProducts] = useState([]);
  const [outletProducts, setOutletProducts] = useState([]);
  // Seed from the last saved config so a reload paints the real hero at once,
  // instead of flashing a default for a second while the fetch runs.
  const [settings, setSettings] = useState(() => getCachedHomepageConfig() || {});
  // A cached config counts as known: it came from the server once and the hero
  // it names is the one that was saved.
  const [configLoaded, setConfigLoaded] = useState(() => Boolean(getCachedHomepageConfig()));

  useEffect(() => {
    let active = true;
    api.get("/seo/organization/jsonld")
      .then((r) => { if (active) setOrgJsonLd(r.data || null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      api.get("/products", { params: { page: 1, page_size: PRODUCT_LIMIT, sort: "newest" } }),
      /* The bestseller flag is set per product in admin, so this rail is a
         merchandising decision rather than a sales calculation. It empties
         itself — and the section disappears — when nothing is flagged. */
      api.get("/products", { params: { page: 1, page_size: PRODUCT_LIMIT, bestseller: true } }),
      /* Outlet zolağı: endirimli mallar öz nişanları (−X%, xətli qiymət) ilə.
         Outlet boşdursa bölmə özü yox olur. */
      api.get("/products", { params: { page: 1, page_size: PRODUCT_LIMIT, outlet: true } }),
      getHomepageConfig().then((data) => ({ data })),
    ]).then(([newResult, bestResult, outletResult, homepageResult]) => {
      if (!active) return;
      if (newResult.status === "fulfilled") setNewProducts(newResult.value.data.items || []);
      if (bestResult.status === "fulfilled") setBestProducts(bestResult.value.data.items || []);
      if (outletResult.status === "fulfilled") setOutletProducts(outletResult.value.data.items || []);
      if (homepageResult.status === "fulfilled") setSettings(homepageResult.value.data || {});
      setConfigLoaded(true);
    });
    return () => { active = false; };
  }, []);

  /* A site that has never opened the slide editor still has a hero: the server
     returns the legacy single-hero columns as one slide, and this is the last
     resort behind even that. */
  const heroFallback = {
    // The bundled photograph is the last resort for a site that has never set a
    // hero — not something to paint while we are still finding out. Painting it
    // first is what made the homepage show one picture and then visibly swap to
    // another a second later.
    image_url: settings.hero_media_url
      || (configLoaded ? (adfAssets.jacket2 || ss26Image(19) || adfAssets.hero) : null),
    // A background video, when the editor has set one. The poster still is
    // shown until it decodes so the hero never flashes empty.
    video_url: settings.hero_video_url || null,
    poster_url: settings.hero_poster_url || settings.hero_media_url || null,
    eyebrow: settings.hero_eyebrow,
    title: settings.hero_title,
    cta_label: settings.hero_cta_label,
    cta_link: settings.hero_cta_link
      || (settings.featured_collection_handle ? `/collections/${settings.featured_collection_handle}` : "/shop"),
  };
  // Video slaydı şəkilsiz də yaşayır — şəkil varsa, o yalnız thumbnail-dır.
  const heroSlides = (settings.hero_slides || []).filter(
    (slide) => slide?.image_url || slide?.video_url || slide?.youtube_url,
  );

  const newRail = useMemo(
    () => newProducts.map((product, index) => compactProduct(product, index, locale)),
    [newProducts, locale],
  );
  const bestRail = useMemo(
    () => bestProducts.map((product, index) => compactProduct(product, index + 40, locale)),
    [bestProducts, locale],
  );
  const outletRail = useMemo(
    () => outletProducts.map((product, index) => compactProduct(product, index + 80, locale)),
    [outletProducts, locale],
  );

  return (
    <div data-testid="home-page" className="adf-home-redesign">
      <SeoMeta
        title="Atelier Di Falco — Keyfiyyət İncəsənəti"
        description={localText(
          locale,
          "Bakıda klassik kişi dərziliyi: kostyumlar, köynəklər və üst geyim. Ölçüyə uyğun seçim, AI ilə virtual sınaq və şəxsi konsyerj xidməti.",
          "Classic menswear tailoring in Baku: suits, shirts and outerwear, with size guidance, AI try-on and a personal concierge.",
        )}
        locale={locale}
        canonicalPath="/"
        jsonLd={orgJsonLd}
      />
      <Header />

      <main id="main">
        <HeroSlider slides={heroSlides} fallback={heroFallback} />

        <Marquee
          text={settings.marquee_hero_text}
          enabled={settings.marquee_hero_enabled !== false}
          locale={locale}
          testId="home-marquee-hero"
        />

        {/* Order per owner: discover-the-collection first, then the latest
            pieces as a 3×4 grid (12 newest) below it. */}
        <CollectionGrid block={settings.collections_grid} />

        {newRail.length > 0 && (
          <ProductRail
            eyebrow={localText(locale, "Yeni gələnlər", "New arrivals")}
            title={localText(locale, "Mövsümün ilk parçaları", "First pieces of the season")}
            copy={localText(
              locale,
              "Kolleksiyaya yeni əlavə olunanlar — məhdud sayda.",
              "The latest additions to the collection, in limited numbers.",
            )}
            cta={localText(locale, "Hamısına bax", "View all")}
            to="/shop"
            items={newRail}
            count={12}
            layout="grid"
            testId="home-new-arrivals"
          />
        )}

        {bestRail.length > 0 && (
          <ProductRail
            eyebrow={localText(locale, "Çox seçilənlər", "Best sellers")}
            title={localText(locale, "Ən çox geyinilənlər", "The most worn pieces")}
            copy={localText(
              locale,
              "Kolleksiyanın ən çox seçilən parçaları.",
              "The pieces our clients return to most.",
            )}
            cta={localText(locale, "Hamısına bax", "View all")}
            to="/shop?sort=bestsellers"
            items={bestRail}
            testId="home-best-sellers"
          />
        )}

        {/* Outlet bölməsi qalereyanın üstündə durur — sahibin istəyi. */}
        {outletRail.length > 0 && (
          <ProductRail
            eyebrow={localText(locale, "Outlet", "Outlet")}
            title={localText(locale, "Keçən mövsüm — xüsusi qiymətlərlə", "Past season, remarkable prices")}
            copy={localText(
              locale,
              "Seçilmiş parçalar outlet qiymətləri ilə — say məhduddur.",
              "Selected pieces at outlet prices — while they last.",
            )}
            cta={localText(locale, "Hamısına bax", "View all")}
            to="/outlet"
            items={outletRail}
            count={8}
            testId="home-outlet"
          />
        )}

        {/* Qalereya zolağı AI bölməsinin üstündə durur — sahibin istəyi. */}
        <GalleryStrip />

        <AITryOnFeature block={settings.tryon_feature} />

        <CampaignBanner block={settings.sale_campaign} variant="sale" testId="home-sale" />

        <CampaignBanner block={settings.perfumery} variant="perfumery" testId="home-perfumery" />

        <ConciergeBand block={settings.concierge_block} />

        <TrustBenefits block={settings.trust_benefits} />

        <NewsletterBlock block={settings.newsletter_block} />

        <Marquee
          text={settings.marquee_footer_text}
          enabled={settings.marquee_footer_enabled !== false}
          locale={locale}
          testId="home-marquee-footer"
        />
      </main>

      {/* The page carries its own newsletter section above. */}
      <Footer showNewsletter={false} />
    </div>
  );
}
